import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Plane, Shirt, Car, TrendingUp, Bitcoin, Laptop, Home as HomeIcon, UtensilsCrossed, Sparkles, Gamepad2, HeartPulse, MapPin } from "lucide-react";
import api from "../lib/api";
import DealCard from "../components/DealCard";
import NewsletterBlock from "../components/NewsletterBlock";
import EditorPick from "../components/EditorPick";
import MerchantStrip from "../components/MerchantStrip";
import TrendingNow from "../components/TrendingNow";
import HomeWebSearchHero from "../components/HomeWebSearchHero";
import { useLocation } from "../lib/LocationContext";

const ICONS = {
  travel: Plane,
  clothing: Shirt,
  auto: Car,
  stocks: TrendingUp,
  crypto: Bitcoin,
  electronics: Laptop,
  home: HomeIcon,
  food: UtensilsCrossed,
  beauty: Sparkles,
  gaming: Gamepad2,
  health: HeartPulse,
};

export default function Home() {
  const { city } = useLocation();
  const [featured, setFeatured] = useState([]);
  const [allDeals, setAllDeals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [healthNear, setHealthNear] = useState([]);
  const [community, setCommunity] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [f, all, cats] = await Promise.all([
          api.get("/deals", { params: { featured: true } }),
          api.get("/deals"),
          api.get("/deals/categories"),
        ]);
        setFeatured(f.data);
        setAllDeals(all.data);
        setCategories(cats.data);
        setCommunity(all.data.filter((d) => d.submitted_by_name).slice(0, 6));
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/deals/near", { params: { category: "health", city: city || undefined, limit: 6 } });
        setHealthNear(r.data);
      } catch (e) { /* ignore */ }
    })();
  }, [city]);

  const heroDeal = featured[0];
  const heroSide = featured.slice(1, 3);
  const editorPicks = allDeals.slice(0, 8);

  return (
    <div data-testid="home-page">
      <EditorPick />
      <HomeWebSearchHero />
      <TrendingNow />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 pt-12 pb-16">
        <div className="grid grid-cols-12 gap-6">
          {/* Left: Editorial statement + Newsletter */}
          <div className="col-span-12 md:col-span-5 flex flex-col justify-between gap-8">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#E63946] mb-4">
                Vol. 01 · The Deals Almanac
              </div>
              <h1 className="font-serif font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mb-6">
                Every&nbsp;deal<br />worth&nbsp;taking,<br /><span className="italic font-normal">in one place.</span>
              </h1>
              <p className="text-base text-black/70 max-w-md mb-6 leading-relaxed">
                A hand-curated directory of the internet&apos;s best savings — travel, fashion, auto, stocks, crypto and beyond. Updated daily by editors who actually read the fine print.
              </p>
              <Link to="/categories" className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 font-bold uppercase tracking-widest text-xs hover:bg-[#E63946]" data-testid="hero-cta-browse">
                Browse all deals <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </Link>
            </div>
            <NewsletterBlock variant="hero" />
          </div>

          {/* Right: Bento of featured */}
          <div className="col-span-12 md:col-span-7 grid grid-cols-6 gap-4">
            {heroDeal && (
              <div className="col-span-6">
                <DealCard deal={heroDeal} size="large" />
              </div>
            )}
            {heroSide.map((d) => (
              <div key={d.id} className="col-span-6 sm:col-span-3">
                <DealCard deal={d} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES STRIP */}
      <section className="border-t border-b border-black/10 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 mb-1">Categories</div>
              <h2 className="font-serif font-bold text-3xl tracking-tight">Shop by section</h2>
            </div>
            <Link to="/categories" className="text-xs font-bold uppercase tracking-widest hover:text-[#E63946]" data-testid="all-categories-link">
              See all →
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {categories.map((c) => {
              const Icon = ICONS[c.slug] || Plane;
              return (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  className="group border border-black/10 p-5 hover:bg-black hover:text-white hover:border-black"
                  data-testid={`category-tile-${c.slug}`}
                >
                  <Icon className="w-6 h-6 mb-4 group-hover:text-[#E63946]" strokeWidth={1.5} />
                  <div className="font-serif font-bold text-lg tracking-tight">{c.name}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <MerchantStrip />

      {/* EDITOR PICKS */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex items-baseline justify-between mb-8">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-2">The Edit</div>
            <h2 className="font-serif font-bold text-4xl md:text-5xl tracking-tight">Editor&apos;s picks this week</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {editorPicks.map((d) => (
            <DealCard key={d.id} deal={d} />
          ))}
        </div>
      </section>

      {/* FROM THE COMMUNITY — surfaces top-contributor bylines */}
      {community.length > 0 && (
        <section className="border-t border-black/10 bg-white">
          <div className="max-w-7xl mx-auto px-6 py-16" data-testid="community-row">
            <div className="flex items-baseline justify-between mb-8">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-2">From the community</div>
                <h2 className="font-serif font-bold text-4xl md:text-5xl tracking-tight">
                  Spotted by our readers.
                </h2>
              </div>
              <Link to="/leaderboard" className="text-xs font-bold uppercase tracking-widest hover:text-[#E63946]" data-testid="all-community-link">
                See the board →
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {community.map((d) => (
                <DealCard key={d.id} deal={d} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* HEALTH & WELLNESS — near you */}
      {healthNear.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-16" data-testid="health-near-you">
          <div className="flex items-baseline justify-between mb-8">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-2 inline-flex items-center gap-1.5">
                <HeartPulse className="w-3.5 h-3.5" strokeWidth={2} />
                Health & Wellness
              </div>
              <h2 className="font-serif font-bold text-4xl md:text-5xl tracking-tight">
                {city ? <>Move. Recover. Save.<br /><span className="italic font-normal text-black/50">Deals in {city}.</span></> : "Move. Recover. Save."}
              </h2>
              {city && (
                <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-black/50">
                  <MapPin className="w-3.5 h-3.5" strokeWidth={2} />
                  Location: {city}
                </div>
              )}
            </div>
            <Link to="/category/health" className="text-xs font-bold uppercase tracking-widest hover:text-[#E63946]" data-testid="all-health-link">
              See all →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {healthNear.map((d) => (
              <DealCard key={d.id} deal={d} />
            ))}
          </div>
        </section>
      )}

      {/* Newsletter banner */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <NewsletterBlock variant="banner" />
      </section>
    </div>
  );
}
